import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  deleteArticleBodyFromR2,
  uploadArticleBodyToR2,
} from "../../services/r2ArticleService";
import {
  FaArrowLeft,
  FaBold,
  FaBookOpen,
  FaCheckCircle,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaPaperPlane,
  FaQuoteRight,
  FaRedo,
  FaUndo,
  FaUnlink,
} from "react-icons/fa";

const ARTICLE_CREATE_LEVELS = new Set([1, 4, 5]);

const canCreateArticle = (user) =>
  ARTICLE_CREATE_LEVELS.has(Number(user?.user_level_id || 0));

const slugifyFilePart = (value) =>
  String(value || "")
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 80) || "guide";

const escapeMarkdown = (value = "") =>
  String(value).replace(/([\\`*_{}[\]()#+\-.!>|])/g, "\\$1");

const getNodeText = (node) =>
  (node?.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n");

function htmlToMarkdown(html = "") {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;

  const walk = (node, context = {}) => {
    if (!node) return "";

    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue?.replace(/\u00a0/g, " ") || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();
    const children = () =>
      Array.from(node.childNodes)
        .map((child) => walk(child, context))
        .join("");

    switch (tag) {
      case "br":
        return "\n";

      case "strong":
      case "b":
        return `**${children()}**`;

      case "em":
      case "i":
        return `*${children()}*`;

      case "u":
        return children();

      case "s":
      case "strike":
      case "del":
        return `~~${children()}~~`;

      case "h1":
        return `# ${children().trim()}\n\n`;

      case "h2":
        return `## ${children().trim()}\n\n`;

      case "h3":
        return `### ${children().trim()}\n\n`;

      case "h4":
        return `#### ${children().trim()}\n\n`;

      case "blockquote":
        return (
          children()
            .trim()
            .split("\n")
            .map((line) => `> ${line}`)
            .join("\n") + "\n\n"
        );

      case "a": {
        const href = node.getAttribute("href") || "";
        const label = children().trim() || href;
        return href ? `[${label}](${href})` : label;
      }

      case "ul":
        return (
          Array.from(node.children)
            .map((child) => walk(child, { ...context, listType: "ul" }))
            .join("") + "\n"
        );

      case "ol":
        return (
          Array.from(node.children)
            .map((child, index) =>
              walk(child, {
                ...context,
                listType: "ol",
                listIndex: index + 1,
              })
            )
            .join("") + "\n"
        );

      case "li": {
        const content = Array.from(node.childNodes)
          .map((child) => walk(child, context))
          .join("")
          .trim();

        const prefix =
          context.listType === "ol"
            ? `${context.listIndex || 1}. `
            : "- ";

        return `${prefix}${content}\n`;
      }

      case "p":
      case "div": {
        const content = children().trimEnd();
        return content ? `${content}\n\n` : "\n";
      }

      case "hr":
        return "\n---\n\n";

      default:
        return children();
    }
  };

  return walk(root)
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}


const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const inlineMarkdownToHtml = (value = "") => {
  let text = escapeHtml(value);

  // Inline code first so later formatting does not interfere with it.
  text = text.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Links.
  text = text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+|mailto:[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Bold, italic and strikethrough.
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  text = text.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  text = text.replace(/~~([^~]+)~~/g, "<s>$1</s>");
  text = text.replace(/\*([^*\n]+)\*/g, "<em>$1</em>");
  text = text.replace(/_([^_\n]+)_/g, "<em>$1</em>");

  return text;
};

const markdownToHtml = (markdown = "") => {
  if (!markdown.trim()) return "";

  const lines = String(markdown).replace(/\r\n?/g, "\n").split("\n");
  const output = [];
  let paragraph = [];
  let listType = null;
  let listItems = [];
  let quoteLines = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    output.push(`<p>${inlineMarkdownToHtml(paragraph.join(" "))}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (!listType || !listItems.length) {
      listType = null;
      listItems = [];
      return;
    }

    const tag = listType === "ol" ? "ol" : "ul";
    output.push(
      `<${tag}>${listItems
        .map((item) => `<li>${inlineMarkdownToHtml(item)}</li>`)
        .join("")}</${tag}>`
    );

    listType = null;
    listItems = [];
  };

  const flushQuote = () => {
    if (!quoteLines.length) return;
    output.push(
      `<blockquote>${quoteLines
        .map((line) => inlineMarkdownToHtml(line))
        .join("<br>")}</blockquote>`
    );
    quoteLines = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
    flushQuote();
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      flushAll();
      return;
    }

    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushAll();
      const level = Math.min(4, heading[1].length);
      output.push(
        `<h${level}>${inlineMarkdownToHtml(heading[2])}</h${level}>`
      );
      return;
    }

    if (/^\s*---+\s*$/.test(line)) {
      flushAll();
      output.push("<hr>");
      return;
    }

    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flushParagraph();
      flushList();
      quoteLines.push(quote[1]);
      return;
    } else {
      flushQuote();
    }

    const unordered = line.match(/^\s*[-*+]\s+(.+)$/);
    if (unordered) {
      flushParagraph();
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listItems.push(unordered[1]);
      return;
    }

    const ordered = line.match(/^\s*\d+\.\s+(.+)$/);
    if (ordered) {
      flushParagraph();
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listItems.push(ordered[1]);
      return;
    }

    flushList();
    paragraph.push(line.trim());
  });

  flushAll();
  return output.join("");
};

export default function NewArticleModal({
  user,
  onClose,
  onArticleCreated,
  mode = "create",
  initialArticle = null,
  initialBodyMarkdown = "",
  loadingInitialBody = false,
  canEditExisting = false,
  onArticleUpdated,
}) {
  const editorRef = useRef(null);
  const initializedEditArticleIdRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const isEditMode = mode === "edit";
  const authorized = isEditMode ? Boolean(canEditExisting) : canCreateArticle(user);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [bodyHtml, setBodyHtml] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase
        .from("article_categories")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error loading categories:", error);
      } else {
        setCategories(data || []);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (!isEditMode || !initialArticle?.id || loadingInitialBody) return;

    const articleChanged =
      initializedEditArticleIdRef.current !== initialArticle.id;

    if (!articleChanged) return;

    const initialHtml = markdownToHtml(initialBodyMarkdown || "");

    setTitle(initialArticle.title || "");
    setSummary(initialArticle.summary || "");
    setCategoryId(initialArticle.category_id || "");
    setBodyHtml(initialHtml);

    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml;
    }

    initializedEditArticleIdRef.current = initialArticle.id;
  }, [
    isEditMode,
    initialArticle,
    initialBodyMarkdown,
    loadingInitialBody,
  ]);

  const selectedCategory = useMemo(
    () =>
      categories.find(
        (category) => String(category.id) === String(categoryId)
      ),
    [categories, categoryId]
  );

  const plainBody = useMemo(() => {
    if (typeof document === "undefined") return "";
    const wrapper = document.createElement("div");
    wrapper.innerHTML = bodyHtml;
    return getNodeText(wrapper).trim();
  }, [bodyHtml]);

  const wordCount = useMemo(() => {
    if (!plainBody) return 0;
    return plainBody.split(/\s+/).filter(Boolean).length;
  }, [plainBody]);

  const rememberEditorSelection = () => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0 || !editorRef.current) {
      return;
    }

    const range = selection.getRangeAt(0);
    const commonNode =
      range.commonAncestorContainer.nodeType === Node.TEXT_NODE
        ? range.commonAncestorContainer.parentElement
        : range.commonAncestorContainer;

    if (commonNode && editorRef.current.contains(commonNode)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreEditorSelection = () => {
    const range = savedSelectionRef.current;

    if (!range || !editorRef.current) {
      editorRef.current?.focus();
      return false;
    }

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    editorRef.current.focus();

    return true;
  };

  const handleEditorSelectionChange = () => {
    rememberEditorSelection();
  };

  const runCommand = (command, value = null) => {
    restoreEditorSelection();
    document.execCommand(command, false, value);
    rememberEditorSelection();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const applyBlock = (tag) => {
    restoreEditorSelection();
    document.execCommand("formatBlock", false, tag);
    rememberEditorSelection();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const toggleQuote = () => {
    restoreEditorSelection();

    const selection = window.getSelection();
    let node = selection?.anchorNode || null;

    if (node?.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    const currentQuote =
      node?.closest?.("blockquote") &&
      editorRef.current?.contains(node.closest("blockquote"))
        ? node.closest("blockquote")
        : null;

    document.execCommand(
      "formatBlock",
      false,
      currentQuote ? "p" : "blockquote"
    );

    rememberEditorSelection();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const addLink = () => {
    rememberEditorSelection();
    const url = window.prompt("Enter the link URL:");
    if (!url) return;

    const safeUrl =
      /^(https?:\/\/|mailto:)/i.test(url) ? url : `https://${url}`;

    restoreEditorSelection();
    document.execCommand("createLink", false, safeUrl);
    rememberEditorSelection();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const handleEditorInput = () => {
    rememberEditorSelection();
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    setBodyHtml(editorRef.current?.innerHTML || "");
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (!authorized) {
      console.warn(
        isEditMode
          ? "Unauthorized article edit attempt blocked."
          : "Unauthorized article creation attempt blocked."
      );
      return;
    }

    if (isEditMode && loadingInitialBody) {
      setSubmitError("The article body is still loading. Please wait a moment.");
      return;
    }

    const cleanTitle = title.trim();
    const cleanSummary = summary.trim();
    const markdownBody = htmlToMarkdown(bodyHtml);

    if (!cleanTitle || !markdownBody) {
      setSubmitError("Title and guide content are required.");
      return;
    }

    setUploading(true);

    let createdArticleId = null;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        setSubmitError("Your session has expired. Please sign in again.");
        return;
      }

      if (isEditMode) {
        if (!initialArticle?.id) {
          throw new Error("No article was supplied for editing.");
        }

        // Capture the currently referenced R2 body before switching the row.
        // Legacy Supabase body_url values are intentionally left untouched.
        const previousR2ObjectKey =
          initialArticle.storage_provider === "r2"
            ? initialArticle.object_key
            : null;

        const storage = await uploadArticleBodyToR2(
          markdownBody,
          initialArticle.id
        );

        const { data, error: updateError } = await supabase
          .from("articles")
          .update({
            title: cleanTitle,
            summary: cleanSummary,
            category_id: categoryId || null,
            storage_provider: storage.storage_provider,
            storage_bucket: storage.storage_bucket,
            object_key: storage.object_key,
            updated_at: new Date().toISOString(),
          })
          .eq("id", initialArticle.id)
          .select()
          .single();

        if (updateError) {
          // Do not delete either the legacy Supabase object or the newly
          // uploaded R2 object automatically during migration.
          throw updateError;
        }

        // The database now safely points at the replacement body. Only now
        // clean up the superseded R2 object. Cleanup failure must not undo a
        // successful article edit; it is logged for later orphan cleanup.
        if (
          previousR2ObjectKey &&
          previousR2ObjectKey !== storage.object_key
        ) {
          try {
            await deleteArticleBodyFromR2(
              initialArticle.id,
              previousR2ObjectKey
            );
          } catch (cleanupError) {
            console.warn(
              "Article saved, but the previous R2 body could not be cleaned up:",
              cleanupError
            );
          }
        }

        onArticleUpdated?.(data, markdownBody);
        return;
      }

      // R2 article uploads are namespaced by article UUID, so create the
      // database row first and then upload its Markdown body.
      const { data: createdArticle, error: insertError } = await supabase
        .from("articles")
        .insert([
          {
            title: cleanTitle,
            summary: cleanSummary,
            category_id: categoryId || null,
            author_id: user.id,
            body_url: null,
            source: "user",
            published_at: new Date().toISOString(),
            featured: false,
          },
        ])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      createdArticleId = createdArticle.id;

      const storage = await uploadArticleBodyToR2(
        markdownBody,
        createdArticle.id
      );

      const { data, error: finalizeError } = await supabase
        .from("articles")
        .update({
          storage_provider: storage.storage_provider,
          storage_bucket: storage.storage_bucket,
          object_key: storage.object_key,
          updated_at: new Date().toISOString(),
        })
        .eq("id", createdArticle.id)
        .select()
        .single();

      if (finalizeError) {
        throw finalizeError;
      }

      createdArticleId = null;
      onArticleCreated?.(data);
      onClose?.();
    } catch (err) {
      // If creation fails after the placeholder row was inserted, remove only
      // that database row. No Supabase Storage objects are deleted here.
      if (createdArticleId) {
        const { error: cleanupError } = await supabase
          .from("articles")
          .delete()
          .eq("id", createdArticleId);

        if (cleanupError) {
          console.warn(
            "Could not clean up failed article database row:",
            cleanupError.message
          );
        }
      }

      console.error(
        isEditMode ? "Failed to save guide changes:" : "Failed to publish guide:",
        err
      );
      setSubmitError(
        `${isEditMode ? "Failed to save guide changes" : "Failed to publish guide"}: ${err?.message || "Unknown error"}`
      );
    } finally {
      setUploading(false);
    }
  };

  if (!authorized) {
    return (
      <section className="guide-rich-editor guide-rich-editor--blocked">
        <style>{styles}</style>

        <div className="guide-rich-editor__blocked-card">
          <h2>Contributor access required</h2>
          <p>
            You do not have permission to create or edit this guide.
          </p>
          <button type="button" onClick={onClose}>
            <FaArrowLeft /> Back to Guides
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="guide-rich-editor">
      <style>{styles}</style>

      <div className="guide-rich-editor__intro">
        <div>
          <span className="guide-rich-editor__eyebrow">
            Hobbli Contributor Studio
          </span>
          <h2>{isEditMode ? "Edit Guide" : "Write a Guide"}</h2>
          <p>
            {isEditMode
              ? "Update the article just like a normal document. Your published Markdown stays behind the scenes."
              : "Write normally, just like you would in a familiar document editor. Hobbli handles the publishing format behind the scenes."}
          </p>
        </div>
      </div>


      {submitError && (
        <div className="guide-rich-editor__notice guide-rich-editor__notice--error" role="alert">
          {submitError}
        </div>
      )}

      <div className="guide-rich-editor__layout">
        <div className="guide-rich-editor__main">
          <div className="guide-rich-editor__field">
            <label htmlFor="guide-title">Title</label>
            <input
              id="guide-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your guide a clear, specific title"
              maxLength={180}
              autoFocus
            />
            <small>{title.length}/180</small>
          </div>

          <div className="guide-rich-editor__field">
            <label htmlFor="guide-summary">Summary</label>
            <textarea
              id="guide-summary"
              className="guide-rich-editor__summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="A short description that helps collectors decide whether this guide is useful."
              maxLength={500}
            />
            <small>{summary.length}/500</small>
          </div>

          <div className="guide-rich-editor__field">
            <label htmlFor="guide-category">Category</label>
            <select
              id="guide-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="guide-rich-editor__document">
            <div className="guide-rich-editor__document-header">
              <div>
                <strong>{isEditMode ? "Article body" : "Guide"}</strong>
                <span>
                  Format text using the toolbar, then keep writing normally.
                </span>
              </div>
              <span>{wordCount.toLocaleString()} words</span>
            </div>

            <div
              className="guide-rich-editor__toolbar"
              role="toolbar"
              aria-label="Text formatting"
              onMouseDown={(event) => {
                if (event.target.closest("button")) {
                  event.preventDefault();
                }
              }}
            >
              <div className="guide-rich-editor__toolbar-group">
                <button
                  type="button"
                  title="Undo"
                  aria-label="Undo"
                  onClick={() => runCommand("undo")}
                >
                  <FaUndo />
                </button>
                <button
                  type="button"
                  title="Redo"
                  aria-label="Redo"
                  onClick={() => runCommand("redo")}
                >
                  <FaRedo />
                </button>
              </div>

              <div className="guide-rich-editor__toolbar-group">
                <select
                  aria-label="Text style"
                  defaultValue=""
                  onMouseDown={rememberEditorSelection}
                  onPointerDown={rememberEditorSelection}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    applyBlock(e.target.value);

                    window.requestAnimationFrame(() => {
                      e.target.value = "";
                    });
                  }}
                >
                  <option value="" disabled>
                    Text style
                  </option>
                  <option value="p">Normal text</option>
                  <option value="h2">Heading</option>
                  <option value="h3">Subheading</option>
                </select>
              </div>

              <div className="guide-rich-editor__toolbar-group">
                <button
                  type="button"
                  title="Bold"
                  aria-label="Bold"
                  onClick={() => runCommand("bold")}
                >
                  <FaBold />
                </button>
                <button
                  type="button"
                  title="Italic"
                  aria-label="Italic"
                  onClick={() => runCommand("italic")}
                >
                  <FaItalic />
                </button>
              </div>

              <div className="guide-rich-editor__toolbar-group">
                <button
                  type="button"
                  title="Bulleted list"
                  aria-label="Bulleted list"
                  onClick={() => runCommand("insertUnorderedList")}
                >
                  <FaListUl />
                </button>
                <button
                  type="button"
                  title="Numbered list"
                  aria-label="Numbered list"
                  onClick={() => runCommand("insertOrderedList")}
                >
                  <FaListOl />
                </button>
                <button
                  type="button"
                  title="Toggle quote"
                  aria-label="Toggle quote"
                  onClick={toggleQuote}
                >
                  <FaQuoteRight />
                </button>
              </div>

              <div className="guide-rich-editor__toolbar-group">
                <button
                  type="button"
                  title="Add link"
                  aria-label="Add link"
                  onClick={addLink}
                >
                  <FaLink />
                </button>
                <button
                  type="button"
                  title="Remove link"
                  aria-label="Remove link"
                  onClick={() => runCommand("unlink")}
                >
                  <FaUnlink />
                </button>
              </div>
            </div>

            <div className="guide-rich-editor__toolbar-hint">
              Tip: click Quote again — or choose Normal text — to return to regular paragraphs.
            </div>

            <div
              className="guide-rich-editor__paper"
              onClick={(event) => {
                if (
                  editorRef.current &&
                  event.target === event.currentTarget
                ) {
                  editorRef.current.focus();
                }
              }}
            >
              {isEditMode && loadingInitialBody ? (
                <div className="guide-rich-editor__body-loading">
                  Loading the published article body...
                </div>
              ) : (
                <div
                  ref={editorRef}
                  className="guide-rich-editor__content"
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Start writing your guide here..."
                  onInput={handleEditorInput}
                  onPaste={handlePaste}
                  onKeyUp={handleEditorSelectionChange}
                  onMouseUp={handleEditorSelectionChange}
                  onFocus={handleEditorSelectionChange}
                  spellCheck
                />
              )}
            </div>
          </div>
        </div>

        <aside className="guide-rich-editor__sidebar">
          <div className="guide-rich-editor__status-card">
            <span className="guide-rich-editor__eyebrow">Publishing</span>
            <h3>Guide details</h3>

            <div className="guide-rich-editor__status-row">
              <span>Author</span>
              <strong>
                {user?.display_name || user?.email || "Hobbli Contributor"}
              </strong>
            </div>

            <div className="guide-rich-editor__status-row">
              <span>Category</span>
              <strong>{selectedCategory?.name || "Not selected"}</strong>
            </div>

            <div className="guide-rich-editor__status-row">
              <span>Source</span>
              <strong>
                {isEditMode
                  ? initialArticle?.source === "hobbli"
                    ? "Hobbli Official"
                    : "Community Guide"
                  : "Community Guide"}
              </strong>
            </div>

            <div className="guide-rich-editor__status-row">
              <span>Status</span>
              <strong className="guide-rich-editor__ready">
                <FaCheckCircle /> Ready when you are
              </strong>
            </div>
          </div>

          <div className="guide-rich-editor__tip-card">
            <FaBookOpen />
            <div>
              <strong>Write for collectors.</strong>
              <p>
                Clear headings, specific examples, and first-hand experience
                make a guide easier to follow and more useful.
              </p>
            </div>
          </div>

          <div className="guide-rich-editor__actions">
            <button
              type="button"
              className="guide-rich-editor__publish"
              onClick={handleSubmit}
              disabled={
                uploading ||
                loadingInitialBody ||
                !title.trim() ||
                !plainBody
              }
            >
              <FaPaperPlane />
              {uploading
                ? isEditMode
                  ? "Saving..."
                  : "Publishing..."
                : isEditMode
                ? "Save Changes"
                : "Publish Guide"}
            </button>

            <button
              type="button"
              className="guide-rich-editor__cancel"
              onClick={onClose}
              disabled={uploading}
            >
              <FaArrowLeft /> {isEditMode ? "Cancel Editing" : "Back to Guides"}
            </button>
          </div>
        </aside>
      </div>
    </section>
  );
}

const styles = `
  .guide-rich-editor {
    --gre-bg: var(--card-bg, #ffffff);
    --gre-text: var(--text, #111827);
    --gre-muted: color-mix(in srgb, var(--text, #111827) 62%, transparent);
    --gre-border: var(--border-color, #dfe4ea);
    --gre-soft: color-mix(in srgb, var(--card-bg, #ffffff) 94%, var(--text, #111827));
    --gre-paper: #ffffff;
    --gre-paper-text: #172033;
    --gre-accent: #2684ff;
    --gre-success: #159957;

    width: 100%;
    color: var(--gre-text);
  }

  .guide-rich-editor,
  .guide-rich-editor * {
    box-sizing: border-box;
  }

  .guide-rich-editor__intro {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--gre-border);
  }

  .guide-rich-editor__eyebrow {
    display: inline-block;
    color: var(--gre-accent);
    font-size: .66rem;
    font-weight: 850;
    letter-spacing: .1em;
    text-transform: uppercase;
  }

  .guide-rich-editor__intro h2 {
    margin: 5px 0 7px;
    font-size: clamp(1.7rem, 4vw, 2.45rem);
    letter-spacing: -.045em;
    line-height: 1.08;
  }

  .guide-rich-editor__intro p {
    max-width: 730px;
    margin: 0;
    color: var(--gre-muted);
    font-size: .82rem;
    line-height: 1.6;
  }


  .guide-rich-editor__notice {
    margin: -8px 0 18px;
    padding: 10px 12px;
    border-radius: 9px;
    font-size: .82rem;
    line-height: 1.45;
  }

  .guide-rich-editor__notice--error {
    background: rgba(180, 35, 24, .10);
    color: #b42318;
    border: 1px solid rgba(180, 35, 24, .18);
  }

  .guide-rich-editor__layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 26px;
    align-items: start;
  }

  .guide-rich-editor__main {
    min-width: 0;
    display: grid;
    gap: 20px;
  }

  .guide-rich-editor__field {
    position: relative;
    display: grid;
    gap: 7px;
  }

  .guide-rich-editor__field > label {
    font-size: .75rem;
    font-weight: 850;
  }

  .guide-rich-editor__field > small {
    position: absolute;
    right: 3px;
    bottom: -17px;
    color: var(--gre-muted);
    font-size: .61rem;
  }

  .guide-rich-editor input,
  .guide-rich-editor select,
  .guide-rich-editor textarea {
    width: 100%;
    border: 1px solid var(--gre-border);
    border-radius: 11px;
    background: var(--gre-soft);
    color: var(--gre-text);
    font: inherit;
    outline: none;
  }

  .guide-rich-editor input,
  .guide-rich-editor select {
    min-height: 48px;
    padding: 11px 13px;
  }

  .guide-rich-editor textarea {
    padding: 12px 13px;
    resize: vertical;
  }

  .guide-rich-editor__summary {
    min-height: 105px;
    line-height: 1.55;
  }

  .guide-rich-editor input:focus,
  .guide-rich-editor select:focus,
  .guide-rich-editor textarea:focus {
    border-color: var(--gre-accent);
    box-shadow: 0 0 0 3px rgba(38, 132, 255, .11);
  }

  .guide-rich-editor__document {
    margin-top: 5px;
    overflow: hidden;
    border: 1px solid var(--gre-border);
    border-radius: 15px;
    background: var(--gre-bg);
  }

  .guide-rich-editor__document-header {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 14px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--gre-border);
    background: var(--gre-soft);
  }

  .guide-rich-editor__document-header > div {
    display: grid;
    gap: 3px;
  }

  .guide-rich-editor__document-header strong {
    font-size: .78rem;
  }

  .guide-rich-editor__document-header span {
    color: var(--gre-muted);
    font-size: .65rem;
  }

  .guide-rich-editor__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    align-items: center;
    padding: 9px 10px;
    border-bottom: 1px solid var(--gre-border);
    background: var(--gre-bg);
  }

  .guide-rich-editor__toolbar-group {
    display: inline-flex;
    gap: 3px;
    align-items: center;
    padding-right: 7px;
    border-right: 1px solid var(--gre-border);
  }

  .guide-rich-editor__toolbar-group:last-child {
    border-right: 0;
    padding-right: 0;
  }

  .guide-rich-editor__toolbar button,
  .guide-rich-editor__toolbar select {
    min-height: 34px;
    border: 1px solid transparent;
    border-radius: 7px;
    background: transparent;
    color: var(--gre-text);
    font: inherit;
    cursor: pointer;
  }

  .guide-rich-editor__toolbar button {
    width: 34px;
    display: grid;
    place-items: center;
    padding: 0;
  }

  .guide-rich-editor__toolbar select {
    width: auto;
    min-width: 128px;
    padding: 4px 8px;
    background: var(--gre-soft);
  }

  .guide-rich-editor__toolbar button:hover,
  .guide-rich-editor__toolbar button:focus-visible {
    border-color: var(--gre-border);
    background: var(--gre-soft);
  }

  .guide-rich-editor__toolbar-hint {
    padding: 7px 12px;
    border-bottom: 1px solid var(--gre-border);
    background: var(--gre-soft);
    color: var(--gre-muted);
    font-size: .62rem;
    line-height: 1.45;
  }

  .guide-rich-editor__paper {
    min-height: 620px;
    padding: clamp(16px, 4vw, 34px);
    background:
      linear-gradient(180deg, rgba(14, 32, 66, .025), transparent 90px),
      var(--gre-soft);
  }

  .guide-rich-editor__body-loading {
    width: min(760px, 100%);
    min-height: 560px;
    margin: 0 auto;
    display: grid;
    place-items: center;
    padding: 30px;
    border: 1px solid rgba(17, 24, 39, .10);
    border-radius: 5px;
    background: var(--gre-paper);
    color: #6b7280;
    font-size: .84rem;
    font-weight: 700;
    text-align: center;
  }

  .guide-rich-editor__content {
    width: min(760px, 100%);
    min-height: 560px;
    margin: 0 auto;
    padding: clamp(25px, 5vw, 48px);
    border: 1px solid rgba(17, 24, 39, .10);
    border-radius: 5px;
    background: #ffffff !important;
    color: #172033 !important;
    caret-color: #172033;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .08);
    outline: none;
    cursor: text;
    user-select: text;
    -webkit-user-select: text;
    pointer-events: auto;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 1rem;
    line-height: 1.75;
  }

  .guide-rich-editor__content p,
  .guide-rich-editor__content div,
  .guide-rich-editor__content span,
  .guide-rich-editor__content li,
  .guide-rich-editor__content h1,
  .guide-rich-editor__content h2,
  .guide-rich-editor__content h3,
  .guide-rich-editor__content h4,
  .guide-rich-editor__content strong,
  .guide-rich-editor__content em {
    color: #172033 !important;
  }

  .guide-rich-editor__content ::selection {
    background: rgba(38, 132, 255, .24);
    color: #111827;
  }

  .guide-rich-editor__content:empty::before {
    content: attr(data-placeholder);
    color: #8992a3;
    pointer-events: none;
  }

  .guide-rich-editor__content:focus {
    border-color: rgba(38, 132, 255, .5);
    box-shadow:
      0 0 0 3px rgba(38, 132, 255, .09),
      0 10px 28px rgba(0, 0, 0, .09);
  }

  .guide-rich-editor__content h2 {
    margin: 1.7em 0 .55em;
    font-family: inherit;
    font-size: 1.55rem;
    line-height: 1.2;
  }

  .guide-rich-editor__content h3 {
    margin: 1.45em 0 .45em;
    font-family: inherit;
    font-size: 1.2rem;
    line-height: 1.25;
  }

  .guide-rich-editor__content blockquote {
    margin: 1.4em 0;
    padding: 10px 18px;
    border-left: 4px solid var(--gre-accent);
    background: #f4f7fb;
    color: #4b5563 !important;
  }

  .guide-rich-editor__content blockquote,
  .guide-rich-editor__content blockquote p,
  .guide-rich-editor__content blockquote div,
  .guide-rich-editor__content blockquote span,
  .guide-rich-editor__content blockquote strong,
  .guide-rich-editor__content blockquote em {
    color: #4b5563 !important;
  }

  .guide-rich-editor__content a {
    color: #1769c2;
  }

  .guide-rich-editor__sidebar {
    position: sticky;
    top: 18px;
    display: grid;
    gap: 12px;
  }

  .guide-rich-editor__status-card,
  .guide-rich-editor__tip-card {
    padding: 17px;
    border: 1px solid var(--gre-border);
    border-radius: 15px;
    background: var(--gre-soft);
  }

  .guide-rich-editor__status-card h3 {
    margin: 5px 0 15px;
    font-size: 1rem;
  }

  .guide-rich-editor__status-row {
    display: grid;
    gap: 3px;
    padding: 10px 0;
    border-top: 1px solid var(--gre-border);
  }

  .guide-rich-editor__status-row > span {
    color: var(--gre-muted);
    font-size: .63rem;
    font-weight: 750;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  .guide-rich-editor__status-row strong {
    overflow-wrap: anywhere;
    font-size: .74rem;
  }

  .guide-rich-editor__ready {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--gre-success) !important;
  }

  .guide-rich-editor__tip-card {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
  }

  .guide-rich-editor__tip-card > svg {
    margin-top: 2px;
    color: var(--gre-accent);
  }

  .guide-rich-editor__tip-card strong {
    font-size: .75rem;
  }

  .guide-rich-editor__tip-card p {
    margin: 4px 0 0;
    color: var(--gre-muted);
    font-size: .7rem;
    line-height: 1.5;
  }

  .guide-rich-editor__actions {
    display: grid;
    gap: 8px;
  }

  .guide-rich-editor__actions button,
  .guide-rich-editor__blocked-card button {
    min-height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 13px;
    border-radius: 11px;
    font: inherit;
    font-size: .76rem;
    font-weight: 850;
    cursor: pointer;
  }

  .guide-rich-editor__publish {
    border: 1px solid var(--gre-accent);
    background: var(--gre-accent);
    color: #fff;
  }

  .guide-rich-editor__publish:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .guide-rich-editor__cancel {
    border: 1px solid var(--gre-border);
    background: var(--gre-bg);
    color: var(--gre-muted);
  }

  .guide-rich-editor--blocked {
    display: grid;
    place-items: center;
    min-height: 440px;
  }

  .guide-rich-editor__blocked-card {
    max-width: 500px;
    padding: 30px;
    border: 1px solid var(--gre-border);
    border-radius: 18px;
    background: var(--gre-soft);
    text-align: center;
  }

  .guide-rich-editor__blocked-card p {
    color: var(--gre-muted);
    line-height: 1.55;
  }

  body.dark .guide-rich-editor,
  .dark .guide-rich-editor {
    --gre-bg: #111827;
    --gre-text: #f3f4f6;
    --gre-muted: #aeb8c5;
    --gre-border: #374151;
    --gre-soft: #182231;
    --gre-paper: #fdfdfd;
    --gre-paper-text: #172033;
  }

  @media (max-width: 980px) {
    .guide-rich-editor__layout {
      grid-template-columns: 1fr;
    }

    .guide-rich-editor__sidebar {
      position: static;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .guide-rich-editor__actions {
      grid-column: 1 / -1;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 700px) {
    .guide-rich-editor__document-header {
      align-items: start;
      flex-direction: column;
    }

    .guide-rich-editor__toolbar {
      gap: 5px;
    }

    .guide-rich-editor__toolbar-group {
      padding-right: 4px;
    }

    .guide-rich-editor__toolbar select {
      min-width: 108px;
    }

    .guide-rich-editor__paper {
      min-height: 520px;
      padding: 8px;
    }

    .guide-rich-editor__content {
      min-height: 500px;
      padding: 22px 18px;
      font-size: .96rem;
    }

    .guide-rich-editor__sidebar {
      grid-template-columns: 1fr;
    }

    .guide-rich-editor__actions {
      grid-column: auto;
      grid-template-columns: 1fr;
    }
  }
`;
